import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { createInvitationSchema } from '@wingseat/shared';
import {
  useAdminInvitations,
  useCreateInvitation,
  useResendInvitation,
} from '../../hooks/use-admin';
import { ApiError } from '../../lib/api-client';
import { formatDate } from '../../lib/format';

export function AdminInvitationsPage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const list = useAdminInvitations();
  const create = useCreateInvitation();
  const resend = useResendInvitation();

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = createInvitationSchema.safeParse({ email });
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message ?? 'Ungültig');
      return;
    }
    setEmailError(null);
    create.mutate(parsed.data, {
      onSuccess: () => setEmail(''),
    });
  };

  const createErr =
    create.error instanceof ApiError ? create.error.message : null;

  return (
    <div className="px-4 py-4 space-y-4">
      <h1 className="text-xl font-semibold">Einladungen</h1>

      <form
        onSubmit={onSubmit}
        className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-2"
      >
        <label className="text-xs text-slate-400">Neue Einladung</label>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            required
            className="flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium hover:bg-brand-700 disabled:opacity-60"
          >
            {create.isPending ? '…' : 'Einladen'}
          </button>
        </div>
        {emailError && <p className="text-xs text-rose-400">{emailError}</p>}
        {createErr && <p className="text-xs text-rose-400">{createErr}</p>}
        <p className="text-xs text-slate-500">
          Es wird automatisch ein User mit Status <code>INVITED</code> angelegt
          und eine Mail mit Registrierungslink versandt.
        </p>
        <p className="text-xs text-slate-500">
          Tipp: CSV-Massenimport unter{' '}
          <Link to="/admin/users/import" className="text-brand-400 underline">
            /admin/users/import
          </Link>
          .
        </p>
      </form>

      {list.isLoading ? (
        <p className="py-12 text-center text-sm text-slate-400">Lädt …</p>
      ) : list.data && list.data.length > 0 ? (
        <ul className="space-y-2">
          {list.data.map((inv) => {
            const expired = new Date(inv.expiresAt) < new Date();
            return (
              <li
                key={inv.id}
                className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm">{inv.email}</p>
                    <p className="text-xs text-slate-500">
                      Erstellt {formatDate(inv.createdAt)}
                      {' · '}läuft ab {formatDate(inv.expiresAt)}
                      {inv.invitedBy &&
                        ` · von ${inv.invitedBy.firstName} ${inv.invitedBy.lastName}`}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${
                      inv.usedAt
                        ? 'bg-emerald-900/40 text-emerald-300 border-emerald-800'
                        : expired
                          ? 'bg-rose-900/40 text-rose-300 border-rose-800'
                          : 'bg-blue-900/40 text-blue-300 border-blue-800'
                    }`}
                  >
                    {inv.usedAt ? 'verwendet' : expired ? 'abgelaufen' : 'offen'}
                  </span>
                </div>
                {!inv.usedAt && (
                  <button
                    onClick={() => resend.mutate(inv.id)}
                    disabled={resend.isPending}
                    className="mt-2 text-xs text-brand-400 hover:underline disabled:opacity-60"
                  >
                    Erneut versenden
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="py-12 text-center text-sm text-slate-400">
          Noch keine Einladungen.
        </p>
      )}
    </div>
  );
}
